import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDropdownModule } from 'ng-zorro-antd/dropdown';
import { NzFlexModule } from 'ng-zorro-antd/flex';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { debounceTime } from 'rxjs/internal/operators/debounceTime';
import { distinctUntilChanged } from 'rxjs/internal/operators/distinctUntilChanged';
import { skip } from 'rxjs/internal/operators/skip';
import { MedServiceSetHeaderRowItemSearchResult } from '../../../../core/api/mhsConfigurationApiClient';
import { ModalityLookup } from '../../../../core/context/modalities-lookup';
import { booleanToString } from '../../../../core/utils/transformers';
import { ProcedureSetsService } from '../../services/procedure-sets-service';
import { Filters } from './procedure-sets-filtes';

@Component({
  selector: 'app-procedure-sets-view',
  imports: [
    CommonModule,
    NzTableModule,
    NzFlexModule,
    NzInputModule,
    FormsModule,
    NzDropdownModule,
    NzButtonModule,
    NzIconModule,
    NzTagModule,
  ],
  templateUrl: './procedure-sets-view.html',
  styleUrl: './procedure-sets-view.scss',
})
export class ProcedureSetsViewComponent {
  readonly router = inject(Router);
  readonly procedureSetsService = inject(ProcedureSetsService);
  readonly searchResult = signal<MedServiceSetHeaderRowItemSearchResult | null>(null);
  readonly loading = signal(true);
  readonly modalities = signal(ModalityLookup.modalities);
  readonly filters = signal<Filters>({
    type: '',
    query: '',
    isMultiple: null,
  });
  total = 0;
  pageSize = 10;
  pageIndex = 1;
  readonly filterObservable = toObservable(this.filters).pipe(
    skip(1),
    debounceTime(500),
    distinctUntilChanged(),
  );

  constructor() {
    this.filterObservable.subscribe((value) => {
      this.onQueryParamsChange({
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        sort: [],
        filter: [],
      });
    });
  }
  queryChange(value: string): void {
    this.filters.update((f) => ({ ...f, query: value }));
  }
  onQueryParamsChange(params: NzTableQueryParams): void {
    const sort = params.sort.find((item) => item.value !== null);
    this.pageIndex = params.pageIndex;
    this.pageSize = params.pageSize;
    this.loading.set(true);

    this.procedureSetsService
      .search(
        this.filters().type ?? undefined,
        this.filters().query,
        booleanToString(this.filters().isMultiple),
        params.pageIndex,
        params.pageSize,
        sort?.key,
        sort?.value === 'ascend',
      )
      .subscribe({
        next: (result) => {
          this.searchResult.set(result);
          this.total = result.total ?? 0;
          this.loading.set(false);
        },
        error: (error) => {
          console.error('Unable to load procedure sets.', error);
          this.loading.set(false);
        },
      });
  }
  detailsClick(medServiceSetId: number): void {
    this.router.navigate(['/procedure-sets', medServiceSetId, 'details']);
  }
  createProcedureSetClick(): void {
    this.router.navigate(['/procedure-sets', 'create']);
  }
  editProcedureSetClick(medServiceSetId: number): void {
    this.router.navigate(['/procedure-sets', medServiceSetId, 'edit']);
  }
}
